import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Favorite, FavoriteBorder } from "@mui/icons-material";
import { BranchProduct } from "../../../types";
import { useProductContext } from "../../../context/ProductContext";
import { useNotification } from "../../../context/NotificationContext";
import RatingStars from "./RatingStars";

interface SmallProductCard1Props {
  product: BranchProduct;
  getCardColors: string;
  getBadgeColor: () => string;
  t: (key: string) => string;
  handleWhatsAppPurchase: (qty: number) => void;
}

const SmallProductCard1: React.FC<SmallProductCard1Props> = ({
  product,
  getCardColors,
  getBadgeColor,
  t,
  handleWhatsAppPurchase,
}) => {
  const navigate = useNavigate();
  const { addToCart } = useProductContext();
  const { notify } = useNotification();
  const [isHovered, setIsHovered] = useState(false);
  const [isWishlist, setIsWishlist] = useState(false);

  const handleAdd = (qty: number) => {
    for (let i = 0; i < qty; i++) addToCart(product.product.id);
    notify("success", t("productCard.addedToCart", ));
  };

  const badge = product.product.badge;
  const images = product.product.images || [];
  const rating = product.product.average_rating || 0;

  const toggleWishlist = () => setIsWishlist(!isWishlist);

  const stockStatus = () => {
    if (!product.is_available || product.stock <= 0) return "bg-red-100 text-red-700";
    if (product.stock <= 3) return "bg-yellow-100 text-yellow-700";
    return "bg-green-100 text-green-700";
  };

  return (
    <div
      data-product-id={product.id}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      <div
        className="relative aspect-square cursor-pointer"
        onClick={() => navigate(`/product/${product.product.id}`)}
      >
        <img
          src={images[0]?.image || "/api/placeholder/250/250"}
          alt={product.product.name}
          className="w-full h-full object-cover"
        />
        {/* Badge */}
        {badge && (
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getBadgeColor()}`}>
              {badge === "sale" ? "خصم" : badge === "new" ? "جديد" : badge}
            </span>
          </div>
        )}

        {/* Wishlist */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleWishlist();
          }}
          className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-sm"
        >
          {isWishlist ? <Favorite className="w-3 h-3 text-red-500" /> : <FavoriteBorder className="w-3 h-3 text-gray-600" />}
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-medium text-gray-800 text-sm line-clamp-2 mb-2 min-h-[2.5rem]">
          {product.product.name}
        </h3>

        {/* Rating */}
        {rating > 0 && <RatingStars rating={rating} layoutVariant="small" />}

        {/* Price */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-base font-bold text-primary">ج.م {product.price}</span>
            {Number(product.product.base_price) > Number(product.price) && (
              <span className="text-xs text-gray-400 line-through block">ج.م {product.product.base_price}</span>
            )}
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${stockStatus()}`}>{product.stock}</span>
        </div>

        {/* Add to Cart */}
        <button
          className={`w-full py-2 bg-gradient-to-r ${getCardColors} text-white text-sm font-medium rounded-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2`}
          onClick={() => handleAdd(1)}
          disabled={!product.is_available}
        >
          <ShoppingCart className="w-4 h-4" />
          {product.is_available ? t("productCard.addToCart") : t("productCard.outOfStock")}
        </button>

        {/* WhatsApp */}
        {product.is_available && (
          <button
            className="mt-2 w-full py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-all duration-300"
            onClick={() => handleWhatsAppPurchase(1)}
          >
            شراء واتساب
          </button>
        )}
      </div>
    </div>
  );
};

export default SmallProductCard1;
